import React, { useState } from 'react';
import clsx from 'clsx';
import { Trash2, Shield, Edit, ArrowRight } from 'lucide-react'; // Added ArrowRight back
import { PlaybookTransformItem, PlaybookTransformType, PlaybookEventConfig } from '@/types/playbook';
import { UnifiedSchema, EventSchema } from '@/types/schema';
import { getTypeColorClass } from '@/utils/schemaUtils';
import EventUsageBar from '@/components/schema/EventUsageBar';
import PlaybookTransformEditor from '@/components/playbooks/transforms-editor/PlaybookTransformEditor';
import { EditorSelection } from '@/components/playbooks/PlaybookEditorTab';
import { getTransformConfig, PlaybookTransformConfig } from '@/config/playbookTransforms';

interface PlaybookTransformTableRowProps {
  transform: PlaybookTransformItem;
  isSelected: boolean;
  onTransformSelect: (transformId: string) => void;
  onDeleteTransform: (transformId: string) => void;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void;
  onSelectionChange: (selection: EditorSelection) => void;
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[];
  allUiTransforms: PlaybookTransformItem[];
  allowUndefinedTransforms: boolean;
  isEditable: boolean;
  getTransformDisplayName: (type: PlaybookTransformType) => string;
  getTransformDetailDisplay: (transform: PlaybookTransformItem) => string;
  getMappingTypeDisplay: (transform: PlaybookTransformItem) => JSX.Element | null;
  getTypeIcon: (dataType?: string) => JSX.Element;
  truncatePath: (path: string, maxLength?: number) => string;
  isPropertyPairApplicableToAllEvents: (inputPath: string | undefined, outputPath: string | undefined, allEvents: EventSchema[]) => boolean; // Add this prop
  getOutputDisplay: (transform: PlaybookTransformItem) => JSX.Element | null;
}

const PlaybookTransformTableRow: React.FC<PlaybookTransformTableRowProps> = ({
  transform,
  isSelected,
  onTransformSelect,
  onDeleteTransform,
  onUpdateTransform,
  onSelectionChange,
  inputSchema,
  outputSchema,
  allEvents,
  uiEventConfigs,
  allUiTransforms,
  allowUndefinedTransforms,
  isEditable,
  getTransformDisplayName,
  getTransformDetailDisplay,
  getMappingTypeDisplay,
  getTypeIcon,
  truncatePath,
  isPropertyPairApplicableToAllEvents, // Destructure this prop
  getOutputDisplay,
}) => {
  const [hoveredTransformId, setHoveredTransformId] = useState<string | null>(null);

  return (
    <>
      <tr
        key={transform.id}
        className={clsx(
          "hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer",
          isSelected ? "bg-blue-50 dark:bg-blue-900/30" : ""
        )}
        onClick={() => onTransformSelect(transform.id)}
        onMouseEnter={() => setHoveredTransformId(transform.id)}
        onMouseLeave={() => setHoveredTransformId(null)}
      >
        <td className="px-6 py-3 text-sm font-medium text-gray-900 dark:text-white">
          <div className="flex items-center gap-2">
            {transform.type === 'enrichment' ? (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                No input
              </span>
            ) : transform.inputPath ? (
              <>
                {transform.inputType && (
                  <span className={`inline-flex w-6 h-6 justify-center items-center rounded-full ${getTypeColorClass(transform.inputType)}`}>
                    {getTypeIcon(transform.inputType)}
                  </span>
                )}
                <span className="font-medium text-sm text-gray-900 dark:text-white truncate" title={transform.inputPath}>
                  {truncatePath(transform.inputPath)}
                </span>
              </>
            ) : (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {transform.type === 'expression' ? 'Multiple inputs' : 
                 transform.type === 'filter' ? 'Event filter' : 
                 'No input'}
              </span>
            )}
          </div>
        </td>
        <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-300">
          {(() => {
            const config = getTransformConfig(transform.type as PlaybookTransformConfig['type']);
            const Icon = config?.icon;
            return (
              <span className={clsx(
                `inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium`,
                config?.colorClass // Use the toned-down color from config
              )}>
                {Icon && <Icon size={14} className={clsx("mr-1", config?.colorClass.replace('bg-', 'text-'))} />}
                {getTransformDetailDisplay(transform) || getTransformDisplayName(transform.type)}
              </span>
            );
          })()}
        </td>
        <td className="px-6 py-2 text-sm text-gray-500 dark:text-gray-300">
          <div className="flex items-center gap-2">
            {transform.type !== 'filter' && ( // Filters don't have a direct output arrow
              <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />
            )}
            {getOutputDisplay(transform)}
          </div>
        </td>
        <td className="px-6 py-3 text-sm text-gray-500 dark:text-gray-300">
          <div className="flex items-center gap-2">
            {transform.isPII && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900 dark:text-fuchsia-200 whitespace-nowrap">
                <Shield size={12} className="mr-1" /> PII
              </span>
            )}
            {transform.mappingType && getMappingTypeDisplay(transform)}
          </div>
        </td>
        <td className="px-6 py-2 text-sm text-gray-500 dark:text-gray-300">
          {transform.eventUsageCount !== undefined && transform.totalEventCount !== undefined && transform.eventNames && (
            <EventUsageBar 
              usedCount={transform.eventUsageCount} 
              totalCount={transform.totalEventCount} 
              eventNames={transform.eventNames} 
              isGlobalTransform={transform.appliesToScope === 'global'} // Pass new prop
            />
          )}
        </td>
        <td className="px-6 py-3 text-right text-sm font-medium">
          <div className={clsx(
            "flex items-center justify-end space-x-2",
            hoveredTransformId === transform.id || isSelected ? 'opacity-100' : 'opacity-0'
          )}>
            {isEditable && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTransformSelect(transform.id);
                  }}
                  className="text-gray-600 hover:text-blue-600 hover:bg-blue-100 dark:text-gray-300 dark:hover:bg-blue-900/50 dark:hover:text-blue-400 p-1 rounded-md"
                  title="Edit Transform"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteTransform(transform.id);
                  }}
                  className="text-gray-600 hover:text-red-600 hover:bg-red-100 dark:text-gray-300 dark:hover:bg-red-900/50 dark:hover:text-red-400 p-1 rounded-md"
                  title="Delete Transform"
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {isSelected && (
        <tr>
          <td colSpan={6} className="px-2 pt-2 pb-3">
            <div>
              <PlaybookTransformEditor
                selectedTransform={transform}
                onUpdateTransform={onUpdateTransform}
                inputSchema={inputSchema}
                outputSchema={outputSchema}
                allEvents={allEvents}
                uiEventConfigs={uiEventConfigs}
                onTransformSelect={() => onTransformSelect('')} // Deselect when editor is closed
                onSelectionChange={onSelectionChange}
                allUiTransforms={allUiTransforms}
                getUsedInputProperties={() => []} // Placeholder, actual logic in PlaybookAIMappingControls
                getUsedOutputProperties={() => []} // Placeholder, actual logic in PlaybookAIMappingControls
                allowUndefinedTransforms={allowUndefinedTransforms}
                isPropertyPairApplicableToAllEvents={(inputPath, outputPath) => isPropertyPairApplicableToAllEvents(inputPath, outputPath, allEvents)} // Pass this prop
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default PlaybookTransformTableRow;
